/**
 * Seasons — «Leer más» para textos largos en móvil.
 *
 * Las descripciones de colección (.seasons-chero__text), las de producto
 * (.seasons-product-description) y cualquier elemento con
 * `data-seasons-readmore` se recortan en móvil (la altura sale de
 * --seasons-readmore-height, ver seasons-mobile-fixes.css) y debajo aparece un
 * botón «Leer más» / «Leer menos». El texto sigue entero en el HTML, así que
 * Google lo lee igual.
 *
 * Si el texto ya cabe, no se recorta ni sale botón. En escritorio no hace nada.
 */
(() => {
  if (window.SeasonsReadmore) return;

  const mq = window.matchMedia('(max-width: 749px)');
  const LABEL_MORE = 'Leer más';
  const LABEL_LESS = 'Leer menos';
  const SELECTOR = '[data-seasons-readmore], .seasons-chero__text, .seasons-product-description';

  /** @param {HTMLElement} el */
  function measure(el) {
    const btn = /** @type {HTMLButtonElement | null} */ (el._seasonsReadmoreBtn);
    if (!btn) return;

    if (!mq.matches) {
      btn.hidden = true;
      return;
    }

    if (el.classList.contains('is-expanded')) {
      btn.hidden = false;
      return;
    }

    // Se mide con el recorte puesto (lo pone el CSS desde el primer pintado).
    const overflows = el.scrollHeight > el.clientHeight + 12;
    el.classList.toggle('is-clamped', overflows);
    btn.hidden = !overflows;
  }

  /** @param {HTMLElement} el */
  function setup(el) {
    const prev = el._seasonsReadmoreBtn;
    if (prev && prev.isConnected) return measure(el);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'seasons-readmore__toggle button-unstyled';
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = LABEL_MORE;
    btn.hidden = true;
    el.insertAdjacentElement('afterend', btn);
    el._seasonsReadmoreBtn = btn;

    btn.addEventListener('click', () => {
      const open = el.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? LABEL_LESS : LABEL_MORE;
      if (!open) {
        const top = el.getBoundingClientRect().top;
        if (top < 0) window.scrollBy({ top: top - 80, behavior: 'smooth' });
      }
    });

    measure(el);
  }

  function init(root = document) {
    root.querySelectorAll(SELECTOR).forEach((el) => setup(/** @type {HTMLElement} */ (el)));
  }

  function remeasureAll() {
    document.querySelectorAll(SELECTOR).forEach((el) => measure(/** @type {HTMLElement} */ (el)));
  }

  window.SeasonsReadmore = { init, remeasureAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  // Las fuentes y las imágenes cambian la altura: se vuelve a medir.
  window.addEventListener('load', remeasureAll, { once: true });
  mq.addEventListener('change', remeasureAll);

  // Secciones repintadas por el tema (variantes, filtros, editor).
  let pending = 0;
  const relevant = (node) =>
    node instanceof HTMLElement &&
    (node.matches(`${SELECTOR}, .seasons-readmore__toggle`) || !!node.querySelector(SELECTOR));

  new MutationObserver((records) => {
    const hit = records.some(
      (r) => [...r.addedNodes].some(relevant) || [...r.removedNodes].some(relevant)
    );
    if (!hit) return;
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(() => init());
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
